window.isPanning = false;

/**
 * SlideManager - Handles dynamic slide loading, caching, and navigation
 */
class SlideManager {
  constructor(config = {}) {
    // Slide order with range support: "XX-YY" for individual slides, "[XX-AA,XX-ZZ]" for ranges
    // Ranges expand into all slides between start and end (inclusive)
    this.slideConfig = config.slides || [];
    this.folderMap = config.folders || {};

    // Expand ranges and flatten into ordered array
    this.slideOrder = this.expandSlideOrder(this.slideConfig);

    // Cache for loaded slide HTML
    this.slideCache = new Map();

    // Cache for parsed slide metadata
    this.slideMetadata = new Map();

    // Track loaded slides
    this.loadedSlides = new Set();

    // Current slide index
    this.currentIndex = 0;

    // Slide container element
    this.container = null;
  }

  /**
   * Expand slide order config, converting ranges into individual slide IDs
   * Supports: "00-01" (single) or "[00-01,00-12]" (range from 00-01 to 00-12)
   */
  expandSlideOrder(config) {
    const expanded = [];

    config.forEach((item) => {
      if (item.startsWith("[") && item.endsWith("]")) {
        // Parse range: "[00-01,00-12]"
        const rangeStr = item.slice(1, -1); // Remove brackets
        const [start, end] = rangeStr.split(",");

        // Parse start and end IDs
        const [startPrefix, startNum] = start.split("-");
        const [endPrefix, endNum] = end.split("-");

        // Both should have same prefix for valid range
        if (startPrefix === endPrefix) {
          const prefix = startPrefix;
          const startInt = parseInt(startNum);
          const endInt = parseInt(endNum);

          // Generate all slides in range
          for (let i = startInt; i <= endInt; i++) {
            const slideNum = String(i).padStart(2, "0");
            expanded.push(`${prefix}-${slideNum}`);
          }
        } else {
          console.warn(`Invalid range: ${item} (prefixes must match)`);
        }
      } else {
        // Single slide ID
        expanded.push(item);
      }
    });

    return expanded;
  }

  /**
   * Initialize the slide manager
   */
  init(containerSelector = "#slide-container") {
    this.container = document.querySelector(containerSelector);
    if (!this.container) {
      console.error("Slide container not found:", containerSelector);
    }

    // Setup touch/swipe navigation
    this.setupTouchNavigation();

    // Scan for existing slides (e.g. inlined intro slide)
    const existingSlides = this.container.querySelectorAll("section[data-slide-id]");
    existingSlides.forEach((slide) => {
      const id = slide.getAttribute("data-slide-id");
      this.loadedSlides.add(id);
      this.parseMetadataFromElement(id, slide);
    });
  }

  /**
   * Setup touch and swipe navigation for mobile
   */
  setupTouchNavigation() {
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50; // Minimum distance to trigger swipe

    this.container.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      false
    );

    this.container.addEventListener(
      "touchend",
      (e) => {
        if (window.isPanning) return;
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX, minSwipeDistance);
      },
      false
    );
  }

  /**
   * Handle swipe gesture
   * Swipe right = previous slide
   * Swipe left = next slide
   */
  handleSwipe(startX, endX, minDistance) {
    const diff = startX - endX;

    // Swipe left = next slide (finger moves left, so startX > endX)
    if (diff > minDistance) {
      this.next();
    }
    // Swipe right = previous slide (finger moves right, so endX > startX)
    else if (diff < -minDistance) {
      this.prev();
    }
  }

  /**
   * Get total number of slides
   */
  get totalSlides() {
    return this.slideOrder.length;
  }

  /**
   * Get current slide ID
   */
  getCurrentSlideId() {
    return this.slideOrder[this.currentIndex];
  }

  /**
   * Get slide ID by index
   */
  getSlideId(index) {
    return this.slideOrder[index];
  }

  /**
   * Get index of a slide ID
   */
  getSlideIndex(slideId) {
    return this.slideOrder.indexOf(slideId);
  }

  /**
   * Get the folder path for a slide based on its ID
   * Pattern: 00 = intro, 01 = primary, 02 = secondary, etc.
   */
  getSlideFolder(slideId) {
    const prefix = slideId.split("-")[0];
    return this.folderMap[prefix] || "intro";
  }

  /**
   * Load a slide's HTML from file
   */
  async loadSlide(slideId) {
    // Return cached version if available
    if (this.slideCache.has(slideId)) {
      return this.slideCache.get(slideId);
    }

    try {
      const folder = this.getSlideFolder(slideId);
      const response = await fetch(`slides/${folder}/${slideId}.html`);
      if (!response.ok) {
        throw new Error(`Failed to load slide: ${slideId}`);
      }

      const html = await response.text();

      // Cache the HTML
      this.slideCache.set(slideId, html);

      // Parse and cache metadata
      this.parseMetadata(slideId, html);

      return html;
    } catch (error) {
      console.error("Error loading slide:", slideId, error);
      return this.getErrorSlideHTML(slideId);
    }
  }

  /**
   * Parse slide metadata from HTML
   */
  parseMetadata(slideId, html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const section = doc.querySelector("section");
    this.parseMetadataFromElement(slideId, section);
  }

  /**
   * Parse slide metadata from DOM element
   */
  parseMetadataFromElement(slideId, section) {
    if (section) {
      const metadata = {
        id: section.getAttribute("data-slide-id") || slideId,
        transition: section.getAttribute("data-transition") || "fade",
        scrollable: section.getAttribute("data-scrollable") === "true",
        title: section.getAttribute("data-title") || "",
      };

      this.slideMetadata.set(slideId, metadata);
    }
  }

  /**
   * Inject slide HTML into DOM
   */
  async injectSlide(slideId) {
    if (this.loadedSlides.has(slideId)) {
      return; // Already in DOM
    }

    const html = await this.loadSlide(slideId);

    // Create temporary container to parse HTML
    const temp = document.createElement("div");
    temp.innerHTML = html;
    const slideElement = temp.firstElementChild;

    if (slideElement && this.container) {
      // Hide by default
      slideElement.classList.add("opacity-0", "pointer-events-none");

      // Append to container
      this.container.appendChild(slideElement);

      // Mark as loaded
      this.loadedSlides.add(slideId);
    }
  }

  /**
   * Show a specific slide by ID
   */
  async showSlide(slideId) {
    // Ensure slide is loaded
    await this.injectSlide(slideId);

    // Hide all slides
    const allSlides = this.container.querySelectorAll("section[data-slide-id]");
    allSlides.forEach((slide) => {
      slide.classList.remove("opacity-100", "pointer-events-auto", "z-10");
      slide.classList.add("opacity-0", "pointer-events-none");
      slide.style.display = "none"; // Ensure hidden
    });

    // Show target slide
    const targetSlide = this.container.querySelector(
      `section[data-slide-id="${slideId}"]`
    );
    if (targetSlide) {
      targetSlide.classList.remove("opacity-0", "pointer-events-none");
      targetSlide.classList.add("opacity-100", "pointer-events-auto", "z-10");
      targetSlide.style.display = ""; // Show with CSS
    }

    // Update current index
    this.currentIndex = this.getSlideIndex(slideId);

    // Update URL hash
    window.location.hash = slideId;
  }

  /**
   * Navigate to next slide
   */
  async next() {
    if (this.currentIndex < this.totalSlides - 1) {
      const nextSlideId = this.slideOrder[this.currentIndex + 1];
      await this.showSlide(nextSlideId);

      // Preload next slide
      this.preloadAdjacent();
    }
  }

  /**
   * Navigate to previous slide
   */
  async prev() {
    if (this.currentIndex > 0) {
      const prevSlideId = this.slideOrder[this.currentIndex - 1];
      await this.showSlide(prevSlideId);

      // Preload adjacent slides
      this.preloadAdjacent();
    }
  }

  /**
   * Navigate to slide by index
   */
  async goToIndex(index) {
    if (index >= 0 && index < this.totalSlides) {
      const slideId = this.slideOrder[index];
      await this.showSlide(slideId);
      this.preloadAdjacent();
    }
  }

  /**
   * Navigate to slide by ID
   */
  async goToSlide(slideId) {
    const index = this.getSlideIndex(slideId);
    if (index !== -1) {
      await this.goToIndex(index);
    }
  }

  /**
   * Preload adjacent slides (next and previous)
   */
  async preloadAdjacent() {
    const preloadPromises = [];

    // Preload next slide
    if (this.currentIndex < this.totalSlides - 1) {
      const nextSlideId = this.slideOrder[this.currentIndex + 1];
      preloadPromises.push(this.injectSlide(nextSlideId));
    }

    // Preload previous slide
    if (this.currentIndex > 0) {
      const prevSlideId = this.slideOrder[this.currentIndex - 1];
      preloadPromises.push(this.injectSlide(prevSlideId));
    }

    await Promise.all(preloadPromises);
  }

  /**
   * Get error slide HTML
   */
  getErrorSlideHTML(slideId) {
    return `
            <section data-slide-id="${slideId}" class="absolute inset-0 flex flex-col justify-center items-center p-8 w-full h-full">
                <h2 class="mb-4 font-light text-4xl text-red-400">Error Loading Slide</h2>
                <p class="text-gray-500">Could not load slide: ${slideId}</p>
            </section>
        `;
  }

  /**
   * Get slide metadata
   */
  getMetadata(slideId) {
    return this.slideMetadata.get(slideId) || {};
  }

  /**
   * Check if at first slide
   */
  isFirst() {
    return this.currentIndex === 0;
  }

  /**
   * Check if at last slide
   */
  isLast() {
    return this.currentIndex === this.totalSlides - 1;
  }
}

import '../css/style.css';
import Alpine from 'alpinejs';

window.Alpine = Alpine;

document.addEventListener('alpine:init', () => {
  Alpine.data("presentation", () => ({
    slideManager: null,
    currentSlide: 0,
    totalSlides: 0,
    isLoading: false,

    async init() {
      console.log("Presentation initialized");

      // Get configuration from global or use default
      const config = window.PRESENTATION_CONFIG || {
        slides: [],
        folders: {}
      };

      // Initialize SlideManager
      this.slideManager = new SlideManager(config);
      this.slideManager.init("#slide-container");
      this.totalSlides = this.slideManager.totalSlides;

      // Check URL hash for initial slide
      const hash = window.location.hash.slice(1);
      if (hash) {
        await this.slideManager.goToSlide(hash);
      } else {
        // Show first slide
        await this.slideManager.showSlide(
          this.slideManager.getCurrentSlideId()
        );
      }

      // Preload adjacent slides
      await this.slideManager.preloadAdjacent();

      // Update current slide index
      this.currentSlide = this.slideManager.currentIndex;

      // Keyboard navigation
      window.addEventListener("keydown", (e) => {
        if (e.key === "ArrowRight") this.next();
        if (e.key === "ArrowLeft") this.prev();
      });

      // Watch for slide changes to update UI
      this.$watch("slideManager.currentIndex", (newIndex) => {
        this.currentSlide = newIndex;
      });

      console.log("Loaded slides:", this.totalSlides);
    },

    async next() {
      if (!this.isLoading && !this.slideManager.isLast()) {
        this.isLoading = true;
        await this.slideManager.next();
        this.currentSlide = this.slideManager.currentIndex;
        this.isLoading = false;
      }
    },

    async prev() {
      if (!this.isLoading && !this.slideManager.isFirst()) {
        this.isLoading = true;
        await this.slideManager.prev();
        this.currentSlide = this.slideManager.currentIndex;
        this.isLoading = false;
      }
    },

    async goToIndex(index) {
      if (!this.isLoading) {
        this.isLoading = true;
        await this.slideManager.goToIndex(index);
        this.currentSlide = this.slideManager.currentIndex;
        this.isLoading = false;
      }
    },

    async goToSlide(slideId) {
      if (!this.isLoading) {
        this.isLoading = true;
        await this.slideManager.goToSlide(slideId);
        this.currentSlide = this.slideManager.currentIndex;
        this.isLoading = false;
      }
    },
  }));

  Alpine.data("pannable", () => ({
    x: 0,
    y: 0,
    startX: 0,
    startY: 0,
    isDragging: false,
    scale: 1,
    initialDistance: 0,
    initialScale: 1,
    width: "100%",
    height: "100%",
    img: null,
    container: null,

    initImage(e) {
      this.img = e.target;
      this.container = this.img.parentElement;
      this.img.style.transformOrigin = "0 0";
      this.updateSize();
      window.addEventListener("resize", () => this.updateSize());

      // Prevent default browser drag behavior
      this.img.addEventListener("dragstart", (e) => e.preventDefault());
    },

    updateSize() {
      if (!this.img || !this.container) return;
      const containerAspect =
        this.container.offsetWidth / this.container.offsetHeight;
      const imgAspect = this.img.naturalWidth / this.img.naturalHeight;

      if (imgAspect > containerAspect) {
        // Image is wider than container
        this.height = "100%";
        this.width = "auto";
      } else {
        // Image is taller than container
        this.width = "100%";
        this.height = "auto";
      }
      // Reset position to center and scale to 1
      this.scale = 1;
      this.$nextTick(() => {
        this.x = (this.container.offsetWidth - this.img.offsetWidth) / 2;
        this.y = (this.container.offsetHeight - this.img.offsetHeight) / 2;
        this.updateTransform();
      });
    },

    updateTransform() {
      if (this.img) {
        this.img.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
      }
    },

    startDrag(e) {
      this.isDragging = true;
      this.img.style.transition = "none"; // Disable transition during drag
      window.isPanning = false;

      if (e.touches && e.touches.length === 2) {
        // Pinch start
        this.initialDistance = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        this.initialScale = this.scale;
      } else {
        // Single touch or mouse
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        this.startX = clientX - this.x;
        this.startY = clientY - this.y;
      }
    },

    drag(e) {
      if (!this.isDragging) return;
      if (e.cancelable) e.preventDefault();
      window.isPanning = true;

      if (e.touches && e.touches.length === 2) {
        // Pinch move
        const currentDistance = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        const newScale = (currentDistance / this.initialDistance) * this.initialScale;
        this.scale = Math.min(1.5, Math.max(1, newScale));
      } else if (e.touches && e.touches.length === 1 || !e.touches) {
        // Pan move
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        let newX = clientX - this.startX;
        let newY = clientY - this.startY;

        this.x = newX;
        this.y = newY;
      }

      // Bounds check (always apply to keep image in view)
      const scaledWidth = this.img.offsetWidth * this.scale;
      const scaledHeight = this.img.offsetHeight * this.scale;
      const minX = this.container.offsetWidth - scaledWidth;
      const minY = this.container.offsetHeight - scaledHeight;

      if (minX < 0) {
        this.x = Math.max(minX, Math.min(0, this.x));
      } else {
        this.x = (this.container.offsetWidth - scaledWidth) / 2;
      }

      if (minY < 0) {
        this.y = Math.max(minY, Math.min(0, this.y));
      } else {
        this.y = (this.container.offsetHeight - scaledHeight) / 2;
      }

      this.updateTransform();
    },

    endDrag() {
      this.isDragging = false;
      this.img.style.transition = ""; // Re-enable transition
      setTimeout(() => {
        window.isPanning = false;
      }, 50);
    },
  }));
});

Alpine.start();
